<template>
	<div>
		<!-- Header and view switch  -->
		<div class="d-flex justify-content-between align-items-center">
			<h3 class="student-subaccount-box--title mb-3">
				{{ $t("bookedClasses") }}
			</h3>
			<div>
				<v-switch
					v-model="tabularView"
					:label="$t('tabular/Calendar View')"
					color="green"
					inset
				></v-switch>
			</div>
		</div>

		<!-- Search inputs with dates -->
		<div v-if="tabularView" class="search">
			<div class="search-dates">
				<v-dialog
					ref="fromDateDialog"
					width="290px"
					v-model="fromDateModal"
					:return-value.sync="search.fromDate"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							:label="$t('fromDate')"
							prepend-icon="mdi-calendar"
							filled
							v-bind="attrs"
							v-on="on"
							color="green"
							clearable
							clear-icon="mdi-close-circle"
							readonly
							v-model="search.fromDate"
						>
						</v-text-field>
					</template>
					<v-date-picker
						v-model="search.fromDate"
						:min="new Date().toISOString()"
					>
						<v-spacer></v-spacer>
						<v-btn text color="red" @click="fromDateModal = false">
							{{ $t("Cancel") }}
						</v-btn>
						<v-btn
							text
							color="green"
							@click="$refs.fromDateDialog.save(search.fromDate)"
						>
							{{ $t("ok") }}
						</v-btn>
					</v-date-picker>
				</v-dialog>
				<v-dialog
					ref="toDateDialog"
					width="290px"
					v-model="toDateModal"
					:return-value.sync="search.toDate"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							:label="$t('toDate')"
							prepend-icon="mdi-calendar"
							filled
							v-bind="attrs"
							v-on="on"
							color="green"
							clearable
							clear-icon="mdi-close-circle"
							readonly
							v-model="search.toDate"
						>
						</v-text-field>
					</template>
					<v-date-picker
						v-model="search.toDate"
						:min="new Date().toISOString()"
					>
						<v-spacer></v-spacer>
						<v-btn text color="red" @click="toDateModal = false">
							{{ $t("Cancel") }}
						</v-btn>
						<v-btn
							text
							color="green"
							@click="$refs.toDateDialog.save(search.toDate)"
						>
							{{ $t("ok") }}
						</v-btn>
					</v-date-picker>
				</v-dialog>
			</div>
			<div class="search-btn-group">
				<v-btn color="success" @click="getTodayClasses" class="me-4">
					{{ $t("search") }}
				</v-btn>
				<v-btn color="error" @click="clearSearch">
					{{ $t("clear") }}
				</v-btn>
			</div>
		</div>

		<!-- Upcoming classes table view -->
		<div v-show="tabularView">
			<v-data-table
				:headers="classesHeadersTitles"
				:items="upcomingSessions"
				item-key="session_id"
				class="elevation-1"
				:items-per-page="10"
				:footer-props="{
					'show-current-page': true,
					showFirstLastPage: true,
					firstIcon: 'mdi-arrow-collapse-left',
					lastIcon: 'mdi-arrow-collapse-right',
					'items-per-page-options': [10, 20, 30, 40, 50, 100],
				}"
				:no-data-text="`${$t('noResultsHere')}`"
			>
				<template v-slot:item.actions="{ item }">
					<v-tooltip top>
						<template v-slot:activator="{ on, attrs }">
							<v-icon
								:data-testId="`reschedule-icon-${item.session_id}`"
								class="mx-1"
								dense
								v-bind="attrs"
								v-on="on"
								@click.stop="showReScheduleDialog(item)"
								v-if="item.reservation_type !== 'Group Class'"
							>
								mdi-calendar-text
							</v-icon>
						</template>
						<span>{{ $t("reschedule") }}</span>
					</v-tooltip>
					<v-tooltip top>
						<template v-slot:activator="{ on, attrs }">
							<v-icon
								:data-testId="`meeting-icon-${item.session_id}`"
								class="mx-1"
								dense
								v-bind="attrs"
								v-on="on"
								@click.stop="showMeetingDialog(item)"
							>
								mdi-information-outline
							</v-icon>
						</template>
						<span>{{ $t("zoomMeetingInformation") }}</span>
					</v-tooltip>
				</template>
			</v-data-table>
		</div>
		<!-- Upcoming classes calendar view -->
		<div v-show="!tabularView">
			<Calendar
				:events="sessions"
				:type="type"
				@update-range="updateSessions"
			/>
		</div>

		<!-- Reschedule dialog -->
		<v-dialog
			transition="dialog-bottom-transition"
			max-width="1400"
			v-model="reScheduleDialog"
			v-if="reScheduleDialog"
		>
			<v-card>
				<v-card-title>
					<span class="text-h3">
						{{ $t("rescheduleSession") }}
					</span>
				</v-card-title>
				<v-card-text class="card-content">
					<SessionsView :id="tutorId" :isHelpVisible="false"/>
				</v-card-text>
				<v-card-actions class="actions-btn-group pe-6">
					<v-btn
						class="action-btn"
						color="green lighten-1"
						@click="reSchedule"
						>{{ $t("reschedule") }}</v-btn
					>
					<v-btn
						class="action-btn"
						color="red darken-3"
						@click="hideReScheduleDialog"
						>{{ $t("Cancel") }}</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Meeting information dialog  -->
		<v-dialog
			transition="dialog-bottom-transition"
			max-width="1200"
			v-model="meetingDialog"
		>
			<v-card class="p-4">
				<v-card-title class="mb-5">
					<h3 class="student-subaccount-box--title m-0">
						{{ $t("zoomMeetingInformation") }}
					</h3>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-card-text>
					<div class="form">
						<div class="form-control">
							<label for="zoom-account" class="form-label">{{
								$t("zoomAccount")
							}}</label>
							<input
								type="text"
								data-testId="zoom-account"
								id="zoom-account"
								class="form-input"
								:value="zoomMeetingData.host_email"
								readonly
							/>
						</div>
						<div class="form-control">
							<label for="date-time" class="form-label">{{
								$t("classDateAndTime")
							}}</label>
							<input
								type="text"
								data-testId="date-time"
								id="date-time"
								class="form-input"
								:value="classDateAndTime"
								readonly
							/>
						</div>
						<div class="form-control">
							<label for="host-id" class="form-label">{{ $t("hostId") }}</label>
							<input
								type="text"
								data-testId="host-id"
								id="host-id"
								class="form-input"
								:value="zoomMeetingData.host_id"
								readonly
								ref="hostId"
							/>
							<!-- <div class="input-actions">
								<v-btn
									color="green lighten-1"
									@click.prevent="copyToClipboard($refs.hostId.value)"
									>{{ $t("copy") }}</v-btn
								>
							</div> -->
						</div>
						<div class="form-control">
							<label for="join-link" class="form-label">{{
								$t("joinLink")
							}}</label>
							<input
								type="text"
								data-testId="join-link"
								id="join-link"
								class="form-input"
								:value="zoomMeetingData.join_meeting_url"
								readonly
								ref="joinLink"
							/>
							<div class="input-actions">
								<v-btn
									data-testId="copy-join-link-btn"
									color="green lighten-1"
									@click.prevent="copyToClipboard($refs.joinLink.value)"
									>{{ $t("copy") }}</v-btn
								>
							</div>
						</div>
					</div>
				</v-card-text>
				<v-card-actions class="d-flex justify-center">
					<v-btn
						data-testId="close-meeting-dialog-btn"
						class="action-btn"
						color="red lighten-1"
						@click="meetingDialog = false"
						>{{ $t("close") }}</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Copy feedback dialog -->
		<v-dialog transition="scale-transition" max-width="200" v-model="copied">
			<v-card class="p-4 d-flex justify-center align-center">
				<p class="m-0">{{ $t("copied") }}</p>
			</v-card>
		</v-dialog>

		<!-- Reschedule requests  -->
		<div class="mb-12 mt-4">
			<h3 class="student-subaccount-box--title mb-6">
				{{ $t("rescheduleRequests") }}
			</h3>
			<v-data-table
				:headers="requestRescheduleHeadersTitles"
				:items="pendingSessions"
				item-key="id"
				class="elevation-1"
				:items-per-page="10"
				:footer-props="{
					'show-current-page': true,
					showFirstLastPage: true,
					firstIcon: 'mdi-arrow-collapse-left',
					lastIcon: 'mdi-arrow-collapse-right',
					'items-per-page-options': [10, 20, 30, 40, 50, 100],
				}"
				:no-data-text="`${$t('noResultsHere')}`"
			>
				<template v-slot:item.actions="{ item }">
					<v-tooltip top>
						<template v-slot:activator="{ on, attrs }">
							<v-icon
								color="blue lighten-2"
								class="mx-1"
								dense
								v-bind="attrs"
								v-on="on"
								@click.stop="acceptReschedule(item)"
							>
								mdi-thumb-up
							</v-icon>
						</template>
						<span>{{ $t("acceptReschedule") }}</span>
					</v-tooltip>
					<v-tooltip top>
						<template v-slot:activator="{ on, attrs }">
							<v-icon
								color="red lighten-2"
								class="mx-1"
								dense
								v-bind="attrs"
								v-on="on"
								@click.stop="refuseReschedule(item)"
							>
								mdi-thumb-down
							</v-icon>
						</template>
						<span>{{ $t("refuseReschedule") }}</span>
					</v-tooltip>
				</template>
			</v-data-table>
		</div>
	</div>
</template>

<script>
import moment from "moment";
import GlobalMixin from "@/GlobalMixin.js";
import SessionsView from "@/views/NewReservation/SessionsView.vue";
import Calendar from "../../global/calendar.vue";

export default {
	name: "StudentClasses",
	mixins: [GlobalMixin],
	components: {
		SessionsView,
		Calendar,
	},
	data() {
		return {
			sessions: [],
			upcomingSessions: [],
			pendingSessions: [],
			currentIns: null,
			userObj: JSON.parse(localStorage.getItem("vuex")),

			tabularView: true,
			windowInnerWidth: window.innerWidth,

			reScheduleDialog: false,
			tutorId: null,
			studentId: null,
			startDateTime: "",
			endDateTime: "",

			search: {
				fromDate: moment(new Date()).format("Y-MM-DD"),
				toDate: moment(new Date()).format("Y-MM-DD"),
			},

			fromDateModal: false,
			toDateModal: false,

			userId: this.$store.getters.StateUser?.user.id,
			meetingDialog: false,
			zoomMeetingData: {},
			classDateAndTime: null,
			copied: false,
		};
	},
	async created() {
		// Get query params for the URL
		this.currentIns = +this.$route.query.ins
			? +this.$route.query.ins
			: this.getInstituteId;

		await this.getTodayClasses();
		this.getCalendarType();
	},
	mounted() {
		this.getPendingSessions();
	},
	methods: {
		async getTodayClasses() {
			if (this.search.fromDate > this.search.toDate) {
				this.showAlert("The from date value is invalid", "error");
				return;
			}

			const upComingsUrl =
				process.env.VUE_APP_APIEndPoint + "reserve/upcomings/";
			const body = {
				user: this.$route.params.id,
				from_date: this.search.fromDate,
				to_date: this.search.toDate,
			};

			await this.axios
				.post(upComingsUrl, body, this.headers)
				.then(res => (this.upcomingSessions = res.data.data.upcoming_sessions))
				.catch(err => this.formatErrors(err));
		},

		getZoomMeetingData(item) {
			if(item.reservation_type === "Group Class") {
				this.zoomMeetingData.join_meeting_url = item.zoom_meeting.join_meeting_url;
				this.zoomMeetingData.host_id = item.zoom_meeting.host_id;
				this.zoomMeetingData.host_email = item.zoom_meeting.host_email;
				return;
			}

			const uid = btoa(this.userId.toString());
			const meetingId = item.zoom_meeting.zoom_meeting_number;
			const mid = btoa(meetingId.toString());

			this.axios
				.post(
					`${process.env.VUE_APP_APIEndPoint}reserve/meeting/`,
					{ uid, mid },
					this.headers
				)
				.then(res => {
					this.zoomMeetingData = res.data.data.zoom_meeting;
				})
				.catch(err => this.formatErrors(err));
		},

		showMeetingDialog(item) {
			this.meetingDialog = true;
			this.classDateAndTime = `${item.start_date}  ${item.start_time}`;
			this.getZoomMeetingData(item);
		},

		async copyToClipboard(value) {
			this.copied = true;
			await navigator.clipboard.writeText(value);
			setTimeout(() => {
				this.copied = false;
			}, 1000);
		},

		/**
		 * Show reSchedule dialog and assign tutorID,startDateTime and endDateTime
		 * @param {*sessionObject} item
		 */
		showReScheduleDialog(item) {
			this.reScheduleDialog = true;
			this.tutorId = item.tutor.id;
			this.studentId = item.student.id;
			this.startDateTime = `${item.start_date} ${item.start_time}`;
			this.endDateTime = `${item.end_date} ${item.end_time}`;
		},

		hideReScheduleDialog() {
			this.reScheduleDialog = false;
			this.tutorId = null;
			this.studentId = null;
			this.startDateTime = " ";
			this.endDateTime = " ";
			this.$store.dispatch("removeSelectedSlot");
		},

		/**
		 * Re-schedule Session
		 */
		reSchedule() {
			if (!this.$store.getters.GET_SELECTED_SLOTS[0]) {
				return this.showAlert("Please select a slot to re-schedule!", "error");
			}

			const body = {
				tutor: this.tutorId,
				student: this.studentId,
				existing_slot: {
					start_date_time: this.startDateTime,
					end_date_time: this.endDateTime,
				},
				requested_slot: {
					start_date_time: this.$store.getters.GET_SELECTED_SLOTS[0]
						.start_date_time,
					end_date_time: this.$store.getters.GET_SELECTED_SLOTS[0]
						.end_date_time,
				},
			};

			this.axios
				.post(
					`${process.env.VUE_APP_APIEndPoint}reserve/session/reschedule-by-operator/`,
					body,
					this.headers
				)
				.then(res => this.showAlert(res.data.data.message, "success"))
				.catch(err => this.formatErrors(err));

			this.hideReScheduleDialog();
		},

		clearSearch() {
			this.search = {
				fromDate: moment(new Date()).format("Y-MM-DD"),
				toDate: moment(new Date()).format("Y-MM-DD"),
			};
		},

		/**
		 * View students sessions
		 * @param student student object
		 * @param from date string
		 * @param to date string
		 */
		showSessions(student, from_date = undefined, to_date = undefined) {
			this.sessions = [];
			const body = {
				user: this.$route.params.id,
				institute: this.currentIns,
				from_date,
				to_date,
			};
			this.axios
				.post(
					`${process.env.VUE_APP_APIEndPoint}reserve/user/sessions/`,
					body,
					this.headers
				)
				.then(res => {
					const sessions = [];
					Object.values(res.data.data.sessions).forEach(day =>
						day.forEach(session => sessions.push(session))
					);
					this.sessions = sessions.map(session => {
						const singleSession = {
							id: session.session_id || session.group_session_id,
							start: new Date(`${session.start_date} ${session.start_time}`),
							end: new Date(`${session.end_date} ${session.end_time}`),
							timed: true,
							start_datetime: moment(
								`${session.start_date} ${session.start_time}`
							),
							end_datetime: moment(`${session.end_date} ${session.end_time}`),
						};

						// Get session name
						switch (session.reservation_type) {
							case "custom_program_course":
								singleSession.name = session.course.course_type_name;
								break;
							case "Group Class":
								singleSession.name = session.name;
								break;
							case "webinar":
								singleSession.name = session.webinar_language_name;
								break;
							default:
								singleSession.name = "Unknown";
						}

						// Get session Color
						const now = new Date().getTime();
						const startDateTime = singleSession.start.getTime();
						const endDateTime = singleSession.end.getTime();

						if (now < endDateTime && now > startDateTime)
							singleSession.color = "grey darken-1";
						else if (now > endDateTime) singleSession.color = "red";
						else if (now < startDateTime) singleSession.color = "green";

						return singleSession;
					});
					this.sessionsDialog = true;
				})
				.catch(err => this.formatErrors(err));
		},

		/**
		 * Update session and calendar
		 * @param options calendar options object
		 */
		updateSessions(options) {
			if (!options?.start?.date) return;
			this.type = options.type;
			const from_date = options.start.date;
			const to_date = options.end.date;
			this.showSessions(this.studentDetails, from_date, to_date);
		},

		async getPendingSessions() {
			const body = {
				user: this.$route.params.id,
				institute: this.$route.query.ins,
				status: [1],
				offset: this.$route.query.offset,
				limit: this.$route.query.limit,
			};

			this.axios
				.post(
					`${process.env.VUE_APP_APIEndPoint}reserve/session/reschedule/list/`,
					body,
					this.headers
				)
				.then(res => {
					this.pendingSessions = res.data.data
						.filter(
							session =>
								moment(session.old_scheduled_utc_start_date_time) >
								moment(new Date())
						)
						.map(session => {
							return {
								...session,
								start_date: moment(
									session.old_scheduled_utc_start_date_time
								).format("DD/MM/YYYY"),
								start_time: moment
									.utc(session.old_scheduled_utc_start_date_time)
									.local()
									.format("HH:mm:ss"),
								requested_date: moment(
									session.requested_scheduled_utc_start_date_time
								).format("DD/MM/YYYY"),
								requested_time: moment
									.utc(session.requested_scheduled_utc_start_date_time)
									.local()
									.format("HH:mm:ss"),
							};
						});
				})
				.catch(err => this.formatErrors(err));
		},

		async updateRescheduleStatus(item, status) {
			const body = {
				tutor: item.requestee.id,
				institute: +this.$route.query.ins,
				requested_slot: {
					start_date_time: moment
						.utc(item.requested_scheduled_utc_start_date_time)
						.local()
						.format("YYYY-MM-DD HH:mm:ss"),
					end_date_time: moment
						.utc(item.requested_scheduled_utc_end_date_time)
						.local()
						.format("YYYY-MM-DD HH:mm:ss"),
				},
				status: status,
			};

			await this.axios
				.post(
					`${process.env.VUE_APP_APIEndPoint}reserve/session/reschedule/update-status/`,
					body,
					this.headers
				)
				.then(res => this.showAlert(res.data.data.message, "success"))
				.catch(err => this.formatErrors(err));

			await this.getPendingSessions();
			await this.getTodayClasses();
		},

		acceptReschedule(item) {
			this.updateRescheduleStatus(item, 2);
		},

		refuseReschedule(item) {
			this.updateRescheduleStatus(item, 3);
		},

		getCalendarType() {
			this.type = this.windowInnerWidth > 767 ? "month" : "day";
		},
	},
	computed: {
		/**
		 * @returns institute id depends on role of the user
		 */
		getInstituteId() {
			return this.userObj.auth.user.user.user_type === "operator"
				? 1
				: +this.userObj.auth.user.user.username.slice("").at(-1);
		},

		classesHeadersTitles() {
			return [
				{ text: this.$t("course_Type"), value: "course_type", sortable: false },
				{ text: this.$t("Start_Date"), value: "start_date", sortable: false },
				{ text: this.$t("Start_Time"), value: "start_time", sortable: false },
				{ text: this.$t("Fees"), value: "session_fee", sortable: false },
				{
					text: this.$t("programName"),
					value: "program.name",
					sortable: false,
				},
				{
					text: this.$t("tutorName"),
					value: "tutor.full_name",
					sortable: false,
				},
				{ text: this.$t("Action"), value: "actions", sortable: false },
			];
		},
		requestRescheduleHeadersTitles() {
			return [
				{
					text: this.$t("tutorName"),
					value: "requestee.full_name",
					sortable: false,
				},
				{ text: this.$t("Start_Date"), value: "start_date", sortable: false },
				{ text: this.$t("Start_Time"), value: "start_time", sortable: false },
				{
					text: this.$t("requestedDate"),
					value: "requested_date",
					sortable: false,
				},
				{
					text: this.$t("requestedTime"),
					value: "requested_time",
					sortable: false,
				},
				{ text: this.$t("Action"), value: "actions", sortable: false },
			];
		},
	},
};
</script>

<style scoped lang="scss">
.search {
	display: grid;
	grid-template-columns: 1fr;
	align-items: center;

	.search-dates {
		display: grid;
		grid-template-columns: 1fr;
		margin-top: 1rem;
	}

	.search-btn-group {
		display: flex;
		justify-content: center;
		margin-bottom: 2rem;
	}
}
.card-content {
	margin-top: -2rem;
}

.form {
	display: grid;
	grid-template-columns: 1fr;
	row-gap: 1rem;
	.form-control {
		display: grid;
		grid-template-columns: 1fr;
		align-items: center;
		gap: 1rem;
		// border: none;
		.form-label {
			text-align: center;
			margin: 0;
		}
		.form-input {
			padding: 0.5rem;
			outline: none;
			border: 2px solid #4caf50;
			border-radius: 9px;
		}
		.input-actions {
			text-align: center;
			button {
				color: #fff;
			}
		}
	}
}
.actions-btn-group {
	display: flex;
	justify-content: center;
	gap: 2rem;
}
.action-btn {
	color: #fff;
}

@media (min-width: 767px) {
	.search {
		grid-template-columns: 1fr 1fr;
		.search-dates {
			grid-template-columns: 1fr 1fr;
			gap: 1rem;
		}

		.search-btn-group {
			justify-content: flex-end;
			margin-bottom: 0;
		}
	}

	.card-content {
		margin-top: -2rem;
	}

	.form {
		.form-control {
			display: grid;
			grid-template-columns: 10rem 1fr 5rem;
		}
	}
}
</style>
